import { TerbebasShopProfile } from './terbebasApi';

// Browser-tab branding (title + favicon) for the tenant whose storefront is
// being served. index.html ships a neutral title and a data: icon so no
// merchant's brand is ever shown on another's tab before the profile lands;
// this module fills both in once GET /storefront/shop resolves.

// Kept module-level so a page that overrides the title (custom pages) can put
// the store's own title back when the shopper navigates away.
let currentProfile: TerbebasShopProfile | null = null;

// A merchant may want a tab caption different from the store's legal/display
// name ("Havasu — Vitamin & Suplemen" vs "Havasu.id"). Lives in the theme blob
// rather than its own column so the whole feature is frontend-only.
export function storeTabTitle(profile: TerbebasShopProfile | null): string {
  const custom = profile?.config?.theme?.browserTabTitle;
  if (typeof custom === 'string' && custom.trim()) return custom.trim();
  return profile?.name ?? '';
}

// Favicon falls back to the store logo: every store already has one, so tabs
// are branded without the merchant touching the new field. A dedicated
// faviconUrl is for stores whose logo is a wide wordmark that reads badly at
// 16px.
export function storeIconUrl(profile: TerbebasShopProfile | null): string | null {
  return profile?.config?.faviconUrl || profile?.config?.logoUrl || null;
}

function applyTitle(title: string) {
  if (title) document.title = title;
}

function applyIcon(url: string | null) {
  if (!url) return;
  // index.html has no real icon link (only the data: placeholder), so
  // find-or-create rather than assuming one exists.
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (link.href !== url) link.href = url;
}

export function applyStoreBranding(profile: TerbebasShopProfile | null) {
  currentProfile = profile;
  applyTitle(storeTabTitle(profile));
  applyIcon(storeIconUrl(profile));
}

// Used by surfaces that temporarily set their own title (e.g. a custom page)
// to restore the store's on unmount.
export function restoreStoreTitle() {
  applyTitle(storeTabTitle(currentProfile));
}
